/* emoji-data — loads base.json + i18n-{locale}.json, exposes Emoji.* registry. */
(function(){
    'use strict';
    const ns = window.Emoji = window.Emoji || {};
    const T  = window.__EMOJI_T  || {};

    const state = {
        ready: false,
        base: null,
        i18n: null,
        emojis: [],                 // sorted records: { h, e, g, s, v, o, hs, s_key }
        byHex: new Map(),
        byChar: new Map(),
        bySubgroup: new Map(),      // subgroup key → hex[]
        groupsByIdx: [],            // [{key, label}]
        subgroupsByKey: new Map(),  // key → {label, groupIdx}
        shortcodes: {},
        shortcodeIndex: new Map(),
        skins: {},
    };

    ns.label = function(hex){
        if (state.i18n && state.i18n.labels && state.i18n.labels[hex]) return state.i18n.labels[hex];
        // Fallback to English label baked into base.json (`n`) — locale-stable.
        const rec = state.byHex.get(hex);
        return (rec && rec.n) || hex;
    };
    /** English label — used for slugs / share-links (locale-stable). */
    ns.labelEn = function(hex){
        const rec = state.byHex.get(hex);
        return (rec && rec.n) || hex;
    };
    ns.tags = function(hex){
        const t = state.i18n && state.i18n.tags && state.i18n.tags[hex];
        return Array.isArray(t) ? t : [];
    };
    ns.groupLabel    = idx  => (state.groupsByIdx[idx] && state.groupsByIdx[idx].label) || '';
    ns.groupKey      = idx  => (state.groupsByIdx[idx] && state.groupsByIdx[idx].key) || '';
    ns.subgroupLabel = key  => (state.subgroupsByKey.get(key) || {}).label || '';
    ns.shortcodes    = hex  => state.shortcodes[hex] || [];
    ns.skinsFor      = hex  => state.skins[hex] || [];
    ns.has           = hex  => state.byHex.has(hex);
    ns.get           = hex  => state.byHex.get(hex);
    ns.byChar        = ch   => state.byChar.get(ch);
    ns.byShortcode   = function(code){
        return state.shortcodeIndex.get(String(code).replace(/^:|:$/g,'').toLowerCase());
    };
    ns.allEmojis = () => state.emojis;
    ns.bySubgroup = key => state.bySubgroup.get(key) || [];
    ns.subgroupsOfGroup = function(groupIdx){
        const out = [];
        const seen = new Set();
        for (const em of state.emojis){
            if (em.g === groupIdx && em.s_key && !seen.has(em.s_key)){
                seen.add(em.s_key);
                out.push(em.s_key);
            }
        }
        return out;
    };
    ns.hexesByGroup = function(groupIdx){
        const out = [];
        for (const em of state.emojis) if (em.g === groupIdx) out.push(em.h);
        return out;
    };
    ns.slug = function(name){
        return String(name || '')
            .toLowerCase()
            .replace(/['’&,()]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 80) || 'emoji';
    };

    let loadPromise = null;
    ns.ready = function(){
        if (loadPromise) return loadPromise;
        loadPromise = Promise.all([
            fetchJson(window.__EMOJI_DATA_BASE),
            fetchJson(window.__EMOJI_DATA_I18N).catch(() => null),
        ]).then(([base, i18n]) => {
            state.base = base;
            state.i18n = i18n;
            ingest(base, i18n);
            state.ready = true;
            return ns;
        }).catch(err => {
            console.error('[emoji-data] load failed', err);
            if (window.__handleError) window.__handleError(err, 'emoji_data_load', { say: T.data_load || 'Failed to load emoji data' });
            else if (window.__toast) window.__toast(T.data_load || 'Failed to load emoji data', 'error');
            throw err;
        });
        return loadPromise;
    };

    function fetchJson(url){
        return fetch(url, { credentials: 'same-origin' }).then(r => {
            if (!r.ok) throw new Error('HTTP ' + r.status + ' ' + url);
            return r.json();
        });
    }

    function ingest(base, i18n){
        // Group labels — locale overlay falls back to English from messages.
        (base.groups || []).forEach(g => {
            const localised = i18n && i18n.groups && i18n.groups[g.order] ? i18n.groups[g.order] : g.message;
            state.groupsByIdx[g.order] = { key: g.key, label: capitalize(localised) };
        });
        // Subgroup labels (groupIdx filled below from emoji walk).
        (base.subgroups || []).forEach(sg => {
            const localised = i18n && i18n.subgroups && i18n.subgroups[sg.key]
                ? i18n.subgroups[sg.key] : sg.message;
            state.subgroupsByKey.set(sg.key, { label: capitalize(localised), groupIdx: -1 });
        });

        // Drop the 26 bare single regional-indicator letters (1F1E6–1F1FF, "🇦".."🇿").
        // They are flag COMPONENTS, not standalone emoji — useless to copy alone, and
        // on Windows they render as boxed letters (A,B,C…) that look like broken flags
        // at the very top of the Flags category. Real flags are indicator PAIRS.
        state.emojis = (base.emojis || [])
            .filter(rec => !/^1F1[E-F][0-9A-F]$/i.test(rec.h))
            .map(rec => ({
                h: rec.h, e: rec.e, n: rec.n || '', g: rec.g, s: rec.s, v: rec.v, o: rec.o ?? 0, hs: !!rec.hs, s_key: '',
            }));
        // `s` is emojibase's GLOBAL subgroup index == the subgroup's `order`, so map
        // it straight to the key. (The old code wrongly assumed a per-group index and
        // walked/guessed the mapping, which leaked subgroups across groups — e.g.
        // People's "skin-tone"/"hairstyle" chips appearing under Animals & Nature.)
        const subgroupByOrder = new Map();
        (base.subgroups || []).forEach(sg => subgroupByOrder.set(sg.order, sg.key));
        state.emojis.forEach(em => { em.s_key = subgroupByOrder.get(em.s) || ''; });

        // Index
        state.byHex.clear();
        state.byChar.clear();
        state.bySubgroup.clear();
        state.emojis.forEach(em => {
            state.byHex.set(em.h, em);
            state.byChar.set(em.e, em.h);
            const meta = state.subgroupsByKey.get(em.s_key);
            if (meta && meta.groupIdx < 0) meta.groupIdx = em.g;
            if (em.s_key){
                if (!state.bySubgroup.has(em.s_key)) state.bySubgroup.set(em.s_key, []);
                state.bySubgroup.get(em.s_key).push(em.h);
            }
        });

        state.shortcodes = base.shortcodes || {};
        state.shortcodeIndex.clear();
        Object.entries(state.shortcodes).forEach(([hex, codes]) => {
            (codes || []).forEach(c => state.shortcodeIndex.set(String(c).toLowerCase(), hex));
        });
        state.skins = base.skins || {};
    }

    function capitalize(s){
        s = String(s || '');
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    ns._state = state;
})();
